'use client';

import {useVirtualizer} from '@tanstack/react-virtual';
import {useEffect, useRef, useState} from 'react';
import {Variant} from '@/types';
import ResponsiveVariantCard from "@/components/variants/responsive-variant-card";
import {ChevronLeft, ChevronRight} from 'lucide-react';
import {Button} from "@/shadcn/components/ui/button";

export function VariantsSliderClient({variants}: { variants: Variant[] }) {
    const parentRef = useRef<HTMLDivElement>(null);
    // Show/hide the arrow buttons based on scroll position
    const [showLeftButton, setShowLeftButton] = useState(false);
    const [showRightButton, setShowRightButton] = useState(false);

    // The virtualizer for horizontal scrolling
    const virtualizer = useVirtualizer({
        horizontal: true,
        count: variants.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 150
    });

    // Listen to scroll events to update arrow button visibility
    useEffect(() => {

        function handleScroll() {
            if (!parentRef.current) return;
            const {scrollLeft, scrollWidth, clientWidth} = parentRef.current;

            // If scrolled away from the left edge, show the left button
            setShowLeftButton(scrollLeft > 0);

            // // If there's more content to the right, show the right button
            setShowRightButton(scrollLeft + clientWidth < scrollWidth);
        }

        const scrollEl = parentRef.current;
        if (scrollEl) {
            scrollEl.addEventListener('scroll', handleScroll);
            // Check on mount
            handleScroll();
            if (!showLeftButton && !showRightButton) {
                setShowRightButton(true);
            }
        }
        return () => {
            scrollEl?.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Smoothly scroll left/right by 200px on button clicks
    const scrollLeft = () => {
        parentRef.current?.scrollBy({left: -400, behavior: 'smooth'});
    };
    const scrollRight = () => {
        parentRef.current?.scrollBy({left: 400, behavior: 'smooth'});
    };

    return (
        <div className="relative w-full h-64 md:h-[22rem] pt-2 lg:pt-8">
            <div
                ref={parentRef}
                className="relative w-full overflow-x-auto overflow-y-hidden h-64 md:h-[21rem]"
            >
                <div
                    className="relative h-full"

                >
                    {virtualizer.getVirtualItems().map((virtualItem) => {
                        const variant = variants[virtualItem.index];
                        const isFirstVisible = virtualItem.index < 3; // Erste 3 priorisieren

                        return (
                            <div
                                key={virtualItem.key}
                                data-index={virtualItem.index}
                                ref={virtualizer.measureElement}
                                className={`absolute top-0 left-0 h-full px-0.5`} // p-2 for a bit of spacing
                                style={{
                                    transform: `translateX(${virtualItem.start}px)`,
                                }}
                            >
                                <div className={'md:w-56 md:h-80 h-60 w-44'}>
                                    <ResponsiveVariantCard variant={variant} priority={isFirstVisible}/>
                                </div>

                            </div>
                        );
                    })}
                </div>
            </div>
            {/* Left scroll button */}
            {showLeftButton && (
                <Button
                    role="button"
                    variant={'outline'}
                    size={'icon'}
                    onClick={scrollLeft}
                    aria-label="Scroll left"
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white rounded p-2 shadow cursor-pointer hover:bg-slate-200 active:bg-slate-200"
                >
                    <ChevronLeft className="w-4 h-4"/>
                </Button>
            )}

            {/* Right scroll button */}
            {showRightButton && (
                <Button
                    variant={'outline'}
                    size={'icon'}
                    onClick={scrollRight}
                    aria-label="Scroll right"
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white rounded p-2 shadow cursor-pointer hover:bg-slate-200 active:bg-slate-200"
                >
                    <ChevronRight className="w-4 h-4"/>
                </Button>
            )}
        </div>

    );
}
